import {Accordion, Button, Col, Form, /* InputGroup, */ Row, Spinner} from 'react-bootstrap'
// import Select from 'react-select'

import {useLazyQuery} from '@apollo/client'
import {useEffect} from 'react'
import {toast} from 'react-toastify'
import {IResStates} from '../../../../types/statesEU'
import {FormikErrors, FormikTouched} from 'formik'
import {GET_ZIP_INFO} from '../../../../gql/queries/leadsQuery'
import {IResZipCode} from '../../../../types/quoteTypes'
import {TFormAllValues} from '../../../policies/policiesTypes'
import {EmploymentIncomeAccordionItem} from './sectionsMemberFormsTabs/EmploymentIncomeAccordionItem'
import {PersonalInformation} from './sectionsMemberFormsTabs/PersonalInformation'
import {LegalDocumentAccordionItem} from './sectionsMemberFormsTabs/LegalDocumentAccordionItem'
import {SpecificInformationMember} from './sectionsMemberFormsTabs/SpecificInformationMember'
import {
  GooglePlacesAutocompleteInput,
  AddressComponent,
  PlaceSelection,
} from 'app/tfi/components/GooglePlacesAutocompleteInput'
import {copyToClipboard} from '../../../../../../utils/clipboardUtils'

interface PolicyHolderProps {
  onNextStep: () => void
  handleChange: (field: string, value: string | boolean | null) => void
  handleBlur: (field: string) => void
  values: TFormAllValues
  errors: FormikErrors<TFormAllValues>
  touched: FormikTouched<TFormAllValues>
  isEdit?: boolean
  dataStates: IResStates | undefined
  updateZips?: number
}

export const PolicyHolderTab: React.FC<PolicyHolderProps> = ({
  onNextStep,
  handleBlur,
  handleChange,
  values,
  errors,
  touched,
  isEdit = false,
  dataStates,
  updateZips,
}) => {
  const [gqlGetMarket, {data: dataZipCode, loading: loadingZipCode, error: errorGetZipCode}] =
    useLazyQuery<IResZipCode>(GET_ZIP_INFO, {
      fetchPolicy: 'no-cache',
    })

  const [
    gqlGetMarketMZip,
    {data: dataMailZipCode, loading: loadingMailZipCode, error: errorGetMailZipCode},
  ] = useLazyQuery<IResZipCode>(GET_ZIP_INFO, {
    fetchPolicy: 'no-cache',
  })

  const handleZipCodeLookup = () => {
    if (!values.zipCode || values.zipCode.length < 3) return

    gqlGetMarket({
      variables: {
        zipCode: values.zipCode,
      },
    })
  }

  const handleMailZipCodeLookup = () => {
    if (!values.mailingZipCode || values.mailingZipCode.length < 3) return

    gqlGetMarketMZip({
      variables: {
        zipCode: values.mailingZipCode,
      },
    })
  }

  const buildCountyOptions = (namesAll?: string | null, fipsAll?: string | null) => {
    const names = namesAll?.split('|') ?? []
    const fips = fipsAll?.split('|') ?? []
    return names.reduce<{name: string; fips: string}[]>((acc, name, index) => {
      const currentFips = fips[index]
      if (!currentFips) return acc
      acc.push({name, fips: currentFips})
      return acc
    }, [])
  }

  const getCountyNameByFips = (options: Array<{name: string; fips: string}>, selectedFips?: string | null) => {
    if (!selectedFips) return ''
    return options.find(({fips}) => fips === selectedFips)?.name ?? ''
  }

  useEffect(() => {
    if (errorGetZipCode) {
      toast.error(`Error: ${errorGetZipCode.message}`)
      return
    }
    if (!dataZipCode) return
    if (dataZipCode.zipcodeByZip) {
      const countyFips = dataZipCode?.zipcodeByZip?.countyFipsAll?.split('|') ?? []
      const stateCode = dataZipCode?.zipcodeByZip.stateId
      const match = dataStates?.allStates?.edges?.find((e) => e.node.code === stateCode)?.node

      values.stateEU = {
        id: match?.id ?? '',
        code: match?.code ?? '',
        name: match?.name ?? '',
      }
      handleChange('stateEU.id', values.stateEU.id)
      handleChange('stateEU.code', values.stateEU.code)
      handleChange('stateEU.name', values.stateEU.name)
      if (dataZipCode?.zipcodeByZip?.city) {
        values.city = dataZipCode.zipcodeByZip.city
        handleChange('city', values.city)
      }

      // Auto-select only if there is at least one and no existing county
      if (countyFips.length >= 1 && !values.countyFips) {
        values.countyFips = countyFips[0]
        handleChange('countyFips', values.countyFips)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataZipCode, errorGetZipCode])

  useEffect(() => {
    if (errorGetMailZipCode) {
      toast.error(`Error: ${errorGetMailZipCode.message}`)
      return
    }
    if (!dataMailZipCode) return
    if (dataMailZipCode.zipcodeByZip) {
      const mailCountyFips = dataMailZipCode?.zipcodeByZip?.countyFipsAll?.split('|') ?? []
      const mailStateCode = dataMailZipCode?.zipcodeByZip.stateId
      const matchMail = dataStates?.allStates?.edges?.find((e) => e.node.code === mailStateCode)?.node

      values.mailingStateeu = {
        id: matchMail?.id ?? '',
        code: matchMail?.code ?? '',
        name: matchMail?.name ?? '',
      }
      handleChange('mailingStateeu.id', values.mailingStateeu.id)
      handleChange('mailingStateeu.code', values.mailingStateeu.code)
      handleChange('mailingStateeu.name', values.mailingStateeu.name)
      if (dataMailZipCode?.zipcodeByZip?.city) {
        values.mailingCity = dataMailZipCode.zipcodeByZip.city
        handleChange('mailingCity', values.mailingCity)
      }

      // Auto-select only if there is at least one and no existing county
      if (mailCountyFips.length >= 1 && !values.mailingCountyFips) {
        values.mailingCountyFips = mailCountyFips[0]
        handleChange('mailingCountyFips', values.mailingCountyFips)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataMailZipCode, errorGetMailZipCode])

  useEffect(() => {
    if (values.zipCode && dataStates?.allStates) handleZipCodeLookup()
    if (values.mailingZipCode && dataStates?.allStates) handleMailZipCodeLookup()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateZips, dataStates])

  const countyOptions = buildCountyOptions(
    dataZipCode?.zipcodeByZip?.countyNamesAll,
    dataZipCode?.zipcodeByZip?.countyFipsAll
  )
  const mailingCountyOptions = buildCountyOptions(
    dataMailZipCode?.zipcodeByZip?.countyNamesAll,
    dataMailZipCode?.zipcodeByZip?.countyFipsAll
  )

  const stateDisplay =
    values.stateEU?.code && values.stateEU?.name
      ? `${values.stateEU.code} - ${values.stateEU.name}`
      : ''

  const selectedStateInfo = dataStates?.allStates?.edges?.find(
    (e) => e.node.code === values.stateEU?.code
  )?.node
  const acaQuoteUrl =
    selectedStateInfo?.jurisdictionType === 'STATE' ? selectedStateInfo?.urlAcaQuote : null

  const mailingStateDisplay =
    values.mailingStateeu?.code && values.mailingStateeu?.name
      ? `${values.mailingStateeu.code} - ${values.mailingStateeu.name}`
      : ''

  const stateName = values.stateEU?.name ?? ''
  const mailingStateName = values.mailingStateeu?.name ?? ''

  const getPart = (components: AddressComponent[] | undefined, ...types: string[]) => {
    if (!components) return ''
    const component = components.find((item) => types.every((type) => item.types.includes(type)))
    return component?.long_name ?? ''
  }

  const getShortPart = (components: AddressComponent[] | undefined, ...types: string[]) => {
    if (!components) return ''
    const component = components.find((item) => types.every((type) => item.types.includes(type)))
    return component?.short_name ?? ''
  }

  const buildStreetAddress = (components: AddressComponent[] | undefined, fallback?: string) => {
    if (!components || components.length === 0) return fallback ?? ''
    const streetNumber = getShortPart(components, 'street_number')
    const route = getPart(components, 'route')
    const streetAddress = [streetNumber, route].filter(Boolean).join(' ').trim()
    return streetAddress || fallback || ''
  }

  const handlePhysicalAddressSelect = ({address, addressComponents}: PlaceSelection) => {
    const streetAddress = buildStreetAddress(addressComponents, address)
    if (streetAddress) {
      handleChange('address1', streetAddress)
    }
    if (!addressComponents) return

    const city =
      getPart(addressComponents, 'locality') ||
      getPart(addressComponents, 'postal_town') ||
      getPart(addressComponents, 'administrative_area_level_2')
    const stateLong = getPart(addressComponents, 'administrative_area_level_1')
    const stateShort = getShortPart(addressComponents, 'administrative_area_level_1')
    const postalCode = getPart(addressComponents, 'postal_code')

    if (city) handleChange('city', city)
    if (postalCode) {
      handleChange('zipCode', postalCode)
      setTimeout(() => handleZipCodeLookup(), 0)
    }
    if (stateLong || stateShort) {
      handleChange('stateEU.code', stateShort || stateLong)
      handleChange('stateEU.name', stateLong || stateShort)
    }
  }

  const handleMailingAddressSelect = ({address, addressComponents}: PlaceSelection) => {
    const streetAddress = buildStreetAddress(addressComponents, address)
    if (streetAddress) {
      handleChange('mailingAddress1', streetAddress)
    }
    if (!addressComponents) return

    const city =
      getPart(addressComponents, 'locality') ||
      getPart(addressComponents, 'postal_town') ||
      getPart(addressComponents, 'administrative_area_level_2')
    const stateLong = getPart(addressComponents, 'administrative_area_level_1')
    const stateShort = getShortPart(addressComponents, 'administrative_area_level_1')
    const postalCode = getPart(addressComponents, 'postal_code')

    if (city) handleChange('mailingCity', city)
    if (postalCode) {
      handleChange('mailingZipCode', postalCode)
      setTimeout(() => handleMailZipCodeLookup(), 0)
    }
    if (stateLong || stateShort) {
      handleChange('mailingStateeu.code', stateShort || stateLong)
      handleChange('mailingStateeu.name', stateLong || stateShort)
    }
  }

  const toSelectValue = (value: boolean | null | undefined) => {
    if (value === true) return 'true'
    if (value === false) return 'false'
    return ''
  }

  const handleNullableBoolean = (field: string, value: string) => {
    const parsed = value === '' ? null : value === 'true'
    handleChange(field, parsed)
  }

  return (
    <div className='policy-holder'>
      <Row>
        <Col sm={12} md={8} className='holder-division'>
          <div className='kt-heading kt-heading--md custom-border-brand'>Holder information</div>
          <PersonalInformation
            membernum={0}
            allValues={values}
            isHolder={true}
            valuesMember={values.holder}
            errors={errors.holder ?? {}}
            touched={touched.holder ?? {}}
            handleChange={(field, value) => handleChange(`holder.${field}`, value)}
            handleBlur={(field) => handleBlur(`holder.${field}`)}
          />
          <Row className='mt-3'>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Owns car</Form.Label>
                <Form.Select
                  value={toSelectValue(values.ownsCar)}
                  onChange={(e) => handleNullableBoolean('ownsCar', e.target.value)}
                >
                  <option value=''>Select...</option>
                  <option value='true'>Yes</option>
                  <option value='false'>No</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Owns home</Form.Label>
                <Form.Select
                  value={toSelectValue(values.ownsHome)}
                  onChange={(e) => handleNullableBoolean('ownsHome', e.target.value)}
                >
                  <option value=''>Select...</option>
                  <option value='true'>Yes</option>
                  <option value='false'>No</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Col>
        <Col sm={12} md={4}>
          <div className='kt-heading kt-heading--md custom-border-brand'>Address information</div>
          <Accordion className='accordion-address' defaultActiveKey='0'>
            <Accordion.Item eventKey='0'>
              <Accordion.Header>Physical address</Accordion.Header>
              <Accordion.Body>
                <Row className='address-section-policy-form'>
                  <Col lg={12}>
                    <Form.Group>
                      <Form.Label className='d-flex align-items-center gap-2'>
                        <i
                          className='bi bi-files'
                          onClick={() => copyToClipboard(values.address1, 'Address Line 1')}
                          title='Copy Address Line 1'
                          style={{fontSize: '0.85rem', cursor: 'pointer', color: '#009ef7'}}
                        ></i>
                        <span>Address Line 1</span>
                      </Form.Label>
                      <GooglePlacesAutocompleteInput
                        placeholder='Enter address'
                        onChange={(e) => handleChange('address1', e.target.value)}
                        onBlur={() => handleBlur('address1')}
                        value={values.address1 ?? ''}
                        onPlaceSelect={handlePhysicalAddressSelect}
                        name='address1'
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={12}>
                    <Form.Group>
                      <Form.Label className='d-flex align-items-center gap-2'>
                        <i
                          className='bi bi-files'
                          onClick={() => copyToClipboard(values.address2, 'Address Line 2')}
                          title='Copy Address Line 2'
                          style={{fontSize: '0.85rem', cursor: 'pointer', color: '#009ef7'}}
                        ></i>
                        <span>Address Line 2</span>
                      </Form.Label>
                      <Form.Control
                        value={values.address2 ?? ''}
                        onChange={(e) => handleChange('address2', e.target.value)}
                        onBlur={() => handleBlur('address2')}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={12}>
                    <Form.Group>
                      <Form.Label className='d-flex align-items-center gap-2'>
                        <i
                          className='bi bi-files'
                          onClick={() => copyToClipboard(values.city, 'City')}
                          title='Copy City'
                          style={{fontSize: '0.85rem', cursor: 'pointer', color: '#009ef7'}}
                        ></i>
                        <span>City</span>
                      </Form.Label>
                      <Form.Control
                        value={values.city ?? ''}
                        onChange={(e) => handleChange('city', e.target.value)}
                        onBlur={() => handleBlur('city')}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={12}>
                    <Form.Group>
                      <Form.Label className='d-flex align-items-center gap-2'>
                        <i
                          className='bi bi-files'
                          onClick={() => copyToClipboard(values.zipCode, 'Zip Code')}
                          title='Copy Zip Code'
                          style={{fontSize: '0.85rem', cursor: 'pointer', color: '#009ef7'}}
                        ></i>
                        <span>
                          Zip Code <small className='text-danger'>{errors.zipCode}</small>
                        </span>
                      </Form.Label>
                      <Form.Control
                        placeholder='Enter zip code'
                        type='text'
                        name='zipCode'
                        value={values.zipCode ?? ''}
                        onChange={(e) => handleChange('zipCode', e.target.value)}
                        onBlur={handleZipCodeLookup}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault() // Evitar submit
                            handleZipCodeLookup()
                          }
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={12}>
                    <Form.Group>
                      <Form.Label className='d-flex align-items-center gap-2'>
                        <i
                          className='bi bi-files'
                          onClick={() => copyToClipboard(stateName, 'State')}
                          title='Copy State'
                          style={{fontSize: '0.85rem', cursor: 'pointer', color: '#009ef7'}}
                        ></i>
                        <span>State</span>
                      </Form.Label>
                      <div className='address-state'>
                        <Form.Control
                          type='text'
                          className='state-full'
                          placeholder='State'
                          value={stateDisplay}
                          readOnly
                        />
                      </div>
                      {acaQuoteUrl && (
                        <a
                          href={acaQuoteUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='btn btn-sm btn-outline-success mt-1 w-100'
                        >
                          <i className='bi bi-box-arrow-up-right me-1' />
                          ACA Quote ({values.stateEU?.code})
                        </a>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={12}>
                    <Form.Group>
                      <Form.Label className='d-flex align-items-center gap-2'>
                        <i
                          className='bi bi-files'
                          onClick={() =>
                            copyToClipboard(
                              getCountyNameByFips(countyOptions, values.countyFips),
                              'County'
                            )
                          }
                          title='Copy County'
                          style={{fontSize: '0.85rem', cursor: 'pointer', color: '#009ef7'}}
                        ></i>
                        <span>County FIPS</span>
                        {loadingZipCode && <Spinner animation='border' size='sm' />}
                      </Form.Label>
                      <Form.Select
                        value={values.countyFips ?? ''}
                        onChange={(e) => {
                          handleChange('countyFips', e.target.value)
                        }}
                      >
                        <option value=''>Select county FIPS</option>
                        {countyOptions.map(({name, fips}) => (
                          <option key={fips} value={fips}>
                            {name} - {fips}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey='1'>
              <Accordion.Header>Mailing Address</Accordion.Header>
              <Accordion.Body>
                <Row className='address-section-policy-form'>
                  <Col lg={12}>
                    <Form.Group>
                      <Form.Label className='d-flex align-items-center gap-2'>
                        <i
                          className='bi bi-files'
                          onClick={() => copyToClipboard(values.mailingAddress1, 'Mailing Address Line 1')}
                          title='Copy Mailing Address Line 1'
                          style={{fontSize: '0.85rem', cursor: 'pointer', color: '#009ef7'}}
                        ></i>
                        <span>Mailing Address Line 1</span>
                      </Form.Label>
                      <GooglePlacesAutocompleteInput
                        placeholder='Enter address'
                        onChange={(e) => handleChange('mailingAddress1', e.target.value)}
                        onBlur={() => handleBlur('mailingAddress1')}
                        value={values.mailingAddress1 ?? ''}
                        onPlaceSelect={handleMailingAddressSelect}
                        name='mailingAddress1'
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={12}>
                    <Form.Group>
                      <Form.Label className='d-flex align-items-center gap-2'>
                        <i
                          className='bi bi-files'
                          onClick={() => copyToClipboard(values.mailingAddress2, 'Mailing Address Line 2')}
                          title='Copy Mailing Address Line 2'
                          style={{fontSize: '0.85rem', cursor: 'pointer', color: '#009ef7'}}
                        ></i>
                        <span>Mailing Address Line 2</span>
                      </Form.Label>
                      <Form.Control
                        value={values.mailingAddress2 ?? ''}
                        onChange={(e) => handleChange('mailingAddress2', e.target.value)}
                        onBlur={() => handleBlur('mailingAddress2')}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={12}>
                    <Form.Group>
                      <Form.Label className='d-flex align-items-center gap-2'>
                        <i
                          className='bi bi-files'
                          onClick={() => copyToClipboard(values.mailingCity, 'Mailing City')}
                          title='Copy Mailing City'
                          style={{fontSize: '0.85rem', cursor: 'pointer', color: '#009ef7'}}
                        ></i>
                        <span>Mailing City</span>
                      </Form.Label>
                      <Form.Control
                        value={values.mailingCity ?? ''}
                        onChange={(e) => handleChange('mailingCity', e.target.value)}
                        onBlur={() => handleBlur('mailingCity')}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={12}>
                    <Form.Group>
                      <Form.Label className='d-flex align-items-center gap-2'>
                        <i
                          className='bi bi-files'
                          onClick={() => copyToClipboard(values.mailingZipCode, 'Mailing Zip Code')}
                          title='Copy Mailing Zip Code'
                          style={{fontSize: '0.85rem', cursor: 'pointer', color: '#009ef7'}}
                        ></i>
                        <span>
                          Zip Code <small className='text-danger'>{errors.zipCode}</small>
                        </span>
                      </Form.Label>
                      <Form.Control
                        placeholder='Enter zip code'
                        type='text'
                        name='zipCode'
                        value={values.mailingZipCode ?? ''}
                        onChange={(e) => handleChange('mailingZipCode', e.target.value)}
                        onBlur={handleMailZipCodeLookup}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault() // Evitar submit
                            handleMailZipCodeLookup()
                          }
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={12}>
                    <Form.Group>
                      <Form.Label className='d-flex align-items-center gap-2'>
                        <i
                          className='bi bi-files'
                          onClick={() => copyToClipboard(mailingStateName, 'Mailing State')}
                          title='Copy Mailing State'
                          style={{fontSize: '0.85rem', cursor: 'pointer', color: '#009ef7'}}
                        ></i>
                        <span>Mailing State</span>
                      </Form.Label>
                      <div className='address-state'>
                        <Form.Control
                          type='text'
                          className='state-full'
                          placeholder='Mailing State'
                          value={mailingStateDisplay}
                          readOnly
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  <Col lg={12}>
                    <Form.Group>
                      <Form.Label className='d-flex align-items-center gap-2'>
                        <i
                          className='bi bi-files'
                          onClick={() =>
                            copyToClipboard(
                              getCountyNameByFips(mailingCountyOptions, values.mailingCountyFips),
                              'Mailing County'
                            )
                          }
                          title='Copy Mailing County'
                          style={{fontSize: '0.85rem', cursor: 'pointer', color: '#009ef7'}}
                        ></i>
                        <span>County FIPS</span>
                        {loadingMailZipCode && <Spinner animation='border' size='sm' />}
                      </Form.Label>
                      <Form.Select
                        value={values.mailingCountyFips ?? ''}
                        onChange={(e) => handleChange('mailingCountyFips', e.target.value)}
                      >
                        <option value=''>Select county FIPS</option>
                        {mailingCountyOptions.map(({name, fips}) => (
                          <option key={fips} value={fips}>
                            {name} - {fips}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Col>
      </Row>
      <Row>
        <Accordion className='my-5'>
          <Accordion.Item eventKey='0'>
            <Accordion.Header>Specific information</Accordion.Header>
            <Accordion.Body>
              <Row>
                <SpecificInformationMember
                  membernum={0}
                  values={values.holder}
                  handleChange={(field, value) => handleChange(`holder.${field}`, value)}
                />
              </Row>
            </Accordion.Body>
          </Accordion.Item>
          {/* Employment information */}
          <EmploymentIncomeAccordionItem
            eventKey='1'
            values={values.holder}
            handleChange={(field, value) => handleChange(`holder.${field}`, value)}
          />
          {/* Legal documents */}
          <LegalDocumentAccordionItem
            eventKey='2'
            membernum={0}
            values={values.holder}
            handleChange={(field, value) => handleChange(`holder.${field}`, value)}
          />
        </Accordion>
      </Row>
      <div className='btn-prev-next'>
        <div></div>
        <Button size='sm' onClick={onNextStep}>
          NEXT STEP
        </Button>
      </div>
    </div>
  )
}
